import React, { useState } from "react";
import { sendPasswordResetEmail } from "firebase/auth";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import auth from "../firebase.init";
import { Helmet } from "react-helmet";
import { X, ArrowLeft, Mail, CheckCircle, AlertTriangle } from "lucide-react";

const ResetPassword = () => {
    const [email, setEmail] = useState("");
    const [resetSent, setResetSent] = useState(false);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const handleResetPassword = async (e) => {
        e.preventDefault();
        
        if (!email.trim()) {
            setError("Please enter your email address");
            return;
        }
        
        setLoading(true);
        try {
            await sendPasswordResetEmail(auth, email);
            setResetSent(true);
            setError("");
        } catch (error) {
            setError(getErrorMessage(error));
        } finally {
            setLoading(false);
        }
    };

    const getErrorMessage = (error) => {
        if (!error) return "";
        switch (error.code) {
            case "auth/user-not-found":
                return "No user found with this email address.";
            case "auth/invalid-email":
                return "Please enter a valid email address.";
            default:
                return "An unexpected error occurred. Please try again later.";
        }
    };

    return (
        <>
            <Helmet>
                <title>Reset Password - Recover Your Account</title>
                <meta name="description" content="Forgot your password? Reset it here and regain access to your account." />
            </Helmet>

            <div className="min-vh-100 d-flex align-items-center position-relative overflow-hidden bg-light">
                {/* Background Shapes */}
                <div 
                    className="position-absolute top-0 start-0 w-100 h-100 opacity-10"
                    style={{ backgroundImage: "url('images/shapes/login-page-shape-1.png')" }}
                />
                <div className="position-absolute top-0 end-0 w-50 h-100 bg-primary opacity-5 rounded-start-5" />
                
                <div className="container position-relative z-10">
                    <div className="row justify-content-center">
                        <div className="col-md-6 col-lg-5">
                            <motion.div 
                                className="card shadow-lg border-0 rounded-4 overflow-hidden"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5 }}
                            >
                                <div className="card-body p-4 p-md-5">
                                    {/* Close Button */}
                                    <div className="text-end mb-4">
                                        <Link 
                                            to="/" 
                                            className="btn-close"
                                            aria-label="Close"
                                        >
                                            <X size={24} />
                                        </Link>
                                    </div>
                                    
                                    {/* Logo */}
                                    <div className="text-center mb-4">
                                       
                                    </div>
                                    
                                    {/* Back to Login */}
                                    <div className="d-flex align-items-center mb-4">
                                        <Link 
                                            to="/login" 
                                            className="d-flex align-items-center text-muted text-decoration-none"
                                        >
                                            <ArrowLeft size={18} className="me-2" />
                                            <span>Back to Login</span>
                                        </Link>
                                    </div>
                                    
                                    {/* Success Message */}
                                    {resetSent ? (
                                        <motion.div 
                                            className="text-center py-3"
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                        >
                                            <div className="mb-4">
                                                <div className="rounded-circle bg-success bg-opacity-10 d-inline-flex align-items-center justify-content-center mb-3" 
                                                     style={{ width: "80px", height: "80px" }}>
                                                    <CheckCircle size={40} className="text-success" />
                                                </div>
                                                <h4 className="mb-3">Password Reset Email Sent!</h4>
                                                <p className="text-muted">
                                                    We've sent instructions to reset your password to <strong>{email}</strong>.
                                                </p>
                                                <p className="mt-3 text-muted">
                                                    <small>
                                                        Didn't receive the email? Check your spam folder or{" "}
                                                        <button 
                                                            onClick={() => {
                                                                setResetSent(false);
                                                                handleResetPassword();
                                                            }}
                                                            className="btn btn-link p-0 text-decoration-none fw-semibold"
                                                        >
                                                            try again
                                                        </button>
                                                    </small>
                                                </p>
                                                <Link 
                                                    to="/login" 
                                                    className="btn btn-primary mt-3 px-4"
                                                >
                                                    Return to Login
                                                </Link>
                                            </div>
                                        </motion.div>
                                    ) : (
                                        <motion.div
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                        >
                                            <h3 className="mb-4">Reset Password</h3>
                                            <p className="text-muted mb-4">
                                                Enter your email address and we'll send you a link to reset your password.
                                            </p>
                                            
                                            {/* Reset Form */}
                                            <form onSubmit={handleResetPassword}>
                                                {error && (
                                                    <div className="alert alert-danger d-flex align-items-center" role="alert">
                                                        <AlertTriangle size={18} className="me-2" />
                                                        <div>{error}</div>
                                                    </div>
                                                )}
                                                
                                                <div className="mb-4">
                                                    <label htmlFor="email" className="form-label">Email Address</label>
                                                    <div className="input-group">
                                                        <span className="input-group-text bg-light border-end-0">
                                                            <Mail size={18} />
                                                        </span>
                                                        <input 
                                                            type="email" 
                                                            className="form-control border-start-0 bg-light"
                                                            id="email"
                                                            value={email}
                                                            onChange={(e) => setEmail(e.target.value)}
                                                            placeholder="Enter your email"
                                                            required
                                                        />
                                                    </div>
                                                </div>
                                                
                                                <div className="d-grid">
                                                    <button 
                                                        type="submit" 
                                                        className="btn btn-primary py-2"
                                                        disabled={loading}
                                                    >
                                                        {loading ? (
                                                            <>
                                                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                                Sending...
                                                            </>
                                                        ) : (
                                                            "Reset Password"
                                                        )}
                                                    </button>
                                                </div>
                                                
                                                <div className="text-center mt-4">
                                                    <p className="mb-0">
                                                        Remember your password?{" "}
                                                        <Link to="/login" className="text-decoration-none fw-semibold">
                                                            Sign in
                                                        </Link>
                                                    </p>
                                                </div>
                                            </form>
                                        </motion.div>
                                    )}
                                </div>
                            </motion.div>
                        </div>
                    </div>
                </div>
            </div>

            <style jsx>{`
                /* Custom styles for Reset Password component */
                .card {
                    transition: transform 0.3s ease, box-shadow 0.3s ease;
                }
                
                .card:hover {
                    transform: translateY(-5px);
                    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1) !important;
                }
                
                .btn-primary {
                    background-color: #4361ee;
                    border-color: #4361ee;
                    transition: all 0.3s ease;
                }
                
                .btn-primary:hover {
                    background-color: #3a56d4;
                    border-color: #3a56d4;
                    transform: translateY(-2px);
                }
                
                .input-group-text {
                    border-right: none;
                }
                
                .form-control:focus {
                    border-color: #4361ee;
                    box-shadow: 0 0 0 0.25rem rgba(67, 97, 238, 0.25);
                }
                
                .form-control.border-start-0:focus {
                    border-left: none;
                }
                
                .alert-danger {
                    background-color: #fff5f5;
                    border-color: #fed7d7;
                    color: #c53030;
                }
                
                /* Animation for success icon */
                @keyframes checkmark {
                    0% {
                        transform: scale(0);
                        opacity: 0;
                    }
                    50% {
                        transform: scale(1.2);
                    }
                    100% {
                        transform: scale(1);
                        opacity: 1;
                    }
                }
                
                .lucide-check-circle {
                    animation: checkmark 0.5s ease-in-out;
                }
                
                /* Responsive adjustments */
                @media (max-width: 768px) {
                    .card-body {
                        padding: 1.5rem !important;
                    }
                }
            `}</style>
        </>
    );
};

export default ResetPassword;